if (!customElements.get('to-video-stories')) {
  class TouchOrbitVideoStories extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;

      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.dialog = this.querySelector('[data-to-video-dialog]');
      this.host = this.querySelector('[data-to-video-host]');
      this.closeButton = this.querySelector('[data-to-video-close]');
      this.muteButton = this.querySelector('[data-to-video-mute]');
      this.dialogTitle = this.querySelector('[data-to-video-dialog-title]');
      this.errorMessage = this.querySelector('[data-to-video-error]');
      this.restoreFocusTarget = null;
      this.currentProvider = '';
      this.currentMuted = true;

      if (!this.dialog || !this.host) return;

      this.addEventListener('click', this.handleClick.bind(this), { signal: this.signal });
      this.dialog.addEventListener('click', this.handleDialogClick.bind(this), { signal: this.signal });
      this.dialog.addEventListener('cancel', this.handleCancel.bind(this), { signal: this.signal });
      this.dialog.addEventListener('keydown', this.handleDialogKeydown.bind(this), { signal: this.signal });
      this.closeButton?.addEventListener('click', () => this.closePlayer(), { signal: this.signal });
      this.muteButton?.addEventListener('click', () => this.toggleMute(), { signal: this.signal });
      window.addEventListener('touchorbit:video-opening', this.handleOtherPlayerOpening.bind(this), {
        signal: this.signal,
      });
    }

    disconnectedCallback() {
      this.closePlayer(false);
      this.controller?.abort();
      this.controller = null;
    }

    handleClick(event) {
      const trigger = event.target.closest('[data-to-video-open]');
      if (!trigger || !this.contains(trigger)) return;

      event.preventDefault();
      this.openPlayer(trigger);
    }

    handleDialogClick(event) {
      if (event.target === this.dialog) this.closePlayer();
    }

    handleCancel(event) {
      event.preventDefault();
      this.closePlayer();
    }

    handleDialogKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closePlayer();
        return;
      }

      if (event.key !== 'Tab' || typeof this.dialog.showModal === 'function') return;

      const focusable = Array.from(
        this.dialog.querySelectorAll('button:not([hidden]):not([disabled]), video[controls], iframe, [tabindex]:not([tabindex="-1"])')
      ).filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleOtherPlayerOpening(event) {
      if (event.detail?.instance !== this && this.isOpen()) this.closePlayer(false);
    }

    openPlayer(trigger) {
      const blockId = trigger.dataset.toVideoOpen;
      const template = Array.from(this.querySelectorAll('[data-to-video-template]')).find(
        (candidate) => candidate.dataset.toVideoTemplate === blockId
      );

      if (!template) return;

      window.dispatchEvent(new CustomEvent('touchorbit:video-opening', { detail: { instance: this } }));
      this.closePlayer(false);

      this.restoreFocusTarget = trigger;
      this.host.replaceChildren(template.content.cloneNode(true));

      const frame = this.host.querySelector('.to-video-player__frame');
      if (!frame) return;

      this.currentProvider = frame.dataset.provider || '';
      this.currentMuted = frame.dataset.startMuted === 'true';
      this.dialogTitle.textContent = frame.dataset.title || 'Video';
      this.errorMessage.hidden = true;
      this.configureMuteButton(frame.dataset.showMute === 'true');

      if (typeof this.dialog.showModal === 'function') {
        try {
          this.dialog.showModal();
        } catch (error) {
          this.dialog.setAttribute('open', '');
        }
      } else {
        this.dialog.setAttribute('open', '');
        this.dialog.setAttribute('aria-modal', 'true');
      }

      document.body.classList.add('to-video-player-open');
      this.initializePlayer(frame);

      window.requestAnimationFrame(() => this.closeButton?.focus({ preventScroll: true }));
    }

    initializePlayer(frame) {
      const video = frame.querySelector('video');
      const shouldAutoplay = frame.dataset.autoplay === 'true';

      if (!video) return;

      video.muted = this.currentMuted;
      video.defaultMuted = this.currentMuted;
      video.addEventListener(
        'volumechange',
        () => {
          this.currentMuted = video.muted || video.volume === 0;
          this.updateMuteButton();
        },
        { signal: this.signal }
      );
      video.addEventListener('error', () => this.showPlaybackError(), { once: true, signal: this.signal });

      if (!shouldAutoplay && !video.controls) {
        video.controls = true;
      }

      if (shouldAutoplay) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            // Sound-on autoplay can be blocked. Native controls remain available when enabled.
            if (!video.controls) video.controls = true;
          });
        }
      }
    }

    configureMuteButton(shouldShow) {
      if (!this.muteButton) return;
      this.muteButton.hidden = !shouldShow;
      this.updateMuteButton();
    }

    updateMuteButton() {
      if (!this.muteButton) return;

      const mutedIcon = this.muteButton.querySelector('[data-icon-muted]');
      const soundIcon = this.muteButton.querySelector('[data-icon-sound]');
      this.muteButton.setAttribute('aria-label', this.currentMuted ? 'Unmute video' : 'Mute video');
      this.muteButton.setAttribute('aria-pressed', String(!this.currentMuted));
      if (mutedIcon) mutedIcon.hidden = !this.currentMuted;
      if (soundIcon) soundIcon.hidden = this.currentMuted;
    }

    toggleMute() {
      const frame = this.host.querySelector('.to-video-player__frame');
      if (!frame) return;

      const video = frame.querySelector('video');
      const iframe = frame.querySelector('iframe');
      this.currentMuted = !this.currentMuted;

      if (video) {
        video.muted = this.currentMuted;
        if (!this.currentMuted && video.paused) {
          const playPromise = video.play();
          playPromise?.catch?.(() => {});
        }
      } else if (iframe?.contentWindow && this.currentProvider === 'external-youtube') {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: this.currentMuted ? 'mute' : 'unMute', args: [] }),
          'https://www.youtube.com'
        );
      } else if (iframe?.contentWindow && this.currentProvider === 'external-vimeo') {
        iframe.contentWindow.postMessage(
          { method: 'setVolume', value: this.currentMuted ? 0 : 1 },
          'https://player.vimeo.com'
        );
      }

      this.updateMuteButton();
    }

    showPlaybackError() {
      if (this.errorMessage) this.errorMessage.hidden = false;
    }

    closePlayer(restoreFocus = true) {
      if (!this.dialog || !this.host) return;

      const video = this.host.querySelector('video');
      if (video) {
        try {
          video.pause();
          video.currentTime = 0;
        } catch (error) {
          // Reset is best-effort for streams that are not seekable.
        }
      }

      this.host.querySelectorAll('iframe').forEach((iframe) => {
        iframe.src = 'about:blank';
      });
      this.host.replaceChildren();

      if (this.dialog.open && typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.dialog.removeAttribute('aria-modal');
      }

      document.body.classList.remove('to-video-player-open');
      if (this.muteButton) this.muteButton.hidden = true;
      if (this.errorMessage) this.errorMessage.hidden = true;

      const focusTarget = this.restoreFocusTarget;
      this.restoreFocusTarget = null;
      this.currentProvider = '';

      if (restoreFocus && focusTarget?.isConnected) {
        window.requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
      }
    }

    isOpen() {
      return Boolean(this.dialog?.open || this.dialog?.hasAttribute('open'));
    }
  }

  customElements.define('to-video-stories', TouchOrbitVideoStories);
}
